import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";
import api, { apiError, fileUrl } from "../lib/api";
import { Camera, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Profile() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({
    name: user?.name || "", bio: user?.bio || "",
    favorite_book: user?.favorite_book || "", location: user?.location || "",
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/users/me", form);
      await refresh();
      toast.success("Profile saved");
    } catch (e2) { toast.error(apiError(e2)); }
    finally { setSaving(false); }
  };

  const onAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/users/me/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refresh();
      toast.success("Photo updated");
    } catch (e2) { toast.error(apiError(e2)); }
    finally { setUploading(false); }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-8">
      <div className="fade-up">
        <p className="text-xs tracking-[0.2em] uppercase text-[#C46B4E] font-semibold">Your Profile</p>
        <h1 className="font-heading text-5xl text-[#2A2621] mt-2">{user?.name}</h1>
        <p className="text-[#615A50] mt-1">{user?.email}{user?.role === "admin" && " · Community Leader"}</p>
      </div>

      <div className="flex items-center gap-5">
        <div className="relative">
          {fileUrl(user?.picture) ? <img src={fileUrl(user.picture)} alt={user.name} className="w-24 h-24 rounded-full object-cover border border-[#D5CEBF]" /> :
            <div className="w-24 h-24 rounded-full bg-[#3D5844] text-white flex items-center justify-center text-3xl font-semibold">{user?.name?.charAt(0)}</div>}
          <label className="absolute -bottom-1 -right-1 w-9 h-9 rounded-full bg-[#C46B4E] text-white flex items-center justify-center cursor-pointer hover:bg-[#AC5B40]" data-testid="avatar-upload-btn">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
            <input type="file" accept="image/*" className="hidden" onChange={onAvatar} data-testid="avatar-input" />
          </label>
        </div>
        <p className="text-sm text-[#615A50]">Add a photo so your circle can recognize you.</p>
      </div>

      <form onSubmit={save} className="space-y-4">
        <div>
          <label className="text-sm font-medium text-[#615A50]">Name</label>
          <input value={form.name} onChange={set("name")} data-testid="profile-name"
            className="mt-1.5 w-full rounded-xl border border-[#D5CEBF] bg-white px-4 py-3 outline-none focus:ring-2 focus:ring-[#3D5844]/40" />
        </div>
        <div>
          <label className="text-sm font-medium text-[#615A50]">About you</label>
          <textarea value={form.bio} onChange={set("bio")} rows={4} data-testid="profile-bio"
            className="mt-1.5 w-full rounded-xl border border-[#D5CEBF] bg-white px-4 py-3 outline-none focus:ring-2 focus:ring-[#3D5844]/40 resize-none" placeholder="Tell the collective about your faith journey…" />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-[#615A50]">Favorite book or verse</label>
            <input value={form.favorite_book} onChange={set("favorite_book")} data-testid="profile-favbook"
              className="mt-1.5 w-full rounded-xl border border-[#D5CEBF] bg-white px-4 py-3 outline-none focus:ring-2 focus:ring-[#3D5844]/40" />
          </div>
          <div>
            <label className="text-sm font-medium text-[#615A50]">Location</label>
            <input value={form.location} onChange={set("location")} data-testid="profile-location"
              className="mt-1.5 w-full rounded-xl border border-[#D5CEBF] bg-white px-4 py-3 outline-none focus:ring-2 focus:ring-[#3D5844]/40" />
          </div>
        </div>
        <button type="submit" disabled={saving} data-testid="profile-save"
          className="inline-flex items-center gap-2 rounded-xl bg-[#3D5844] text-white px-6 py-3 font-medium hover:bg-[#2F4535] transition-colors disabled:opacity-60">
          {saving && <Loader2 className="w-4 h-4 animate-spin" />} Save profile
        </button>
      </form>
    </div>
  );
}
